import { useCallback, useEffect, useMemo } from 'react'
import toast from 'react-hot-toast'
import { Button, GenericFullscreenDialog } from '@/common'
import { DownloadArrowDownIcon } from '@/common/components/icons'
import {
  useDownloadImportedGoodLiabilityOverviewPdf,
  type BatchLiabilityResultItem
} from '@/features/cbam-reporting/hooks/cbamLiability'
import type { ImportedGood } from '@/features/cbam-reporting/hooks/importedGoods'
import type { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'
import type { CbamModifiers } from '@/shared/types/cbamModifiers'
import useI18n from '@/shared/hooks/usei18n'
import { trackCbamReportingSignal } from '@/features/cbam-reporting/analytics/cbamReportingClarity'
import {
  buildLiabilityOverviewModel,
  LiabilityOverviewTemplate
} from '@/shared/components/cbam-good-details/liability-overview'
import { useNetMassDisplay } from '@/shared/netMassDisplay/NetMassDisplayUnitContext'

type ImportedGoodLiabilityOverviewDialogProps = {
  isOpen: boolean
  close: () => void
  importedGood: ImportedGood
  liabilityData: BatchLiabilityResultItem
  supplierEmissionsReport: SupplierEmissionsReport | null
  modifiers: CbamModifiers
  reportingPeriodYear: number
  aggregatedGoodsCategory?: string
}

export default function ImportedGoodLiabilityOverviewDialog({
  isOpen,
  close,
  importedGood,
  liabilityData,
  supplierEmissionsReport,
  modifiers,
  reportingPeriodYear,
  aggregatedGoodsCategory
}: ImportedGoodLiabilityOverviewDialogProps) {
  const { t, currentLanguage } = useI18n()
  const { unit: displayUnit } = useNetMassDisplay()

  const { mutateAsync: downloadPdf, isPending: isDownloadingPdf } =
    useDownloadImportedGoodLiabilityOverviewPdf(importedGood.id)

  const goodFacts = useMemo(
    () => ({
      quarter: importedGood.quarter,
      cnCode: importedGood.cnCode,
      description: importedGood.description,
      netMassInTonnes: importedGood.netMassInTonnes,
      isUsingDefaultEmissions: importedGood.isUsingDefaultEmissions,
      country: importedGood.country
    }),
    [importedGood]
  )

  const model = useMemo(
    () =>
      buildLiabilityOverviewModel({
        liabilityData,
        good: goodFacts,
        supplierEmissionsReport,
        modifiers,
        projectionYear: reportingPeriodYear,
        aggregatedGoodsCategory,
        t
      }),
    [liabilityData, goodFacts, supplierEmissionsReport, modifiers, reportingPeriodYear, aggregatedGoodsCategory, t]
  )

  useEffect(() => {
    if (isOpen) {
      trackCbamReportingSignal('reporting:good:liability-explanation:opened')
    }
  }, [isOpen])

  const onDownloadPdf = useCallback(async () => {
    const cnCode = model?.cnCode ?? importedGood.cnCode
    const year = model?.year ?? reportingPeriodYear
    const filename = `liability-overview-${cnCode}-${year}.pdf`.replace(/\s/g, '_')
    try {
      await downloadPdf({ filename, lang: currentLanguage })
    } catch (err) {
      console.error(err)
      toast.error(t('common:liabilityExplanationDialog.errors.downloadFailed'), {
        position: 'top-center'
      })
    }
  }, [downloadPdf, model, importedGood.cnCode, reportingPeriodYear, currentLanguage, t])

  return (
    <GenericFullscreenDialog
      isOpen={isOpen}
      close={close}
      title={t('common:liabilityExplanationDialog.title')}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="subtle"
            icon={<DownloadArrowDownIcon />}
            onClick={onDownloadPdf}
            disabled={isDownloadingPdf || !model}
            isLoading={isDownloadingPdf}
          >
            {t('common:liabilityExplanationDialog.actions.downloadPdf')}
          </Button>
          <Button variant="subtle" onClick={close}>
            {t('common:actions.close')}
          </Button>
        </div>
      }
      containerSize="5xl"
    >
      <LiabilityOverviewTemplate
        model={model}
        originCountryCode={importedGood.country}
        displayUnit={displayUnit}
      />
    </GenericFullscreenDialog>
  )
}
